import React, { useEffect, useState } from 'react';
import { IActivityType, IAsset } from "../models/IAsset";
import { Card, Row, Col, Image, ListGroup, Button, Modal } from 'react-bootstrap';
import {
    Trash2,
    Stethoscope,
    Barcode,
    MapPin,
    ClipboardX,
    ClipboardPen,
    List,
    ThumbsUp,
    Wrench,
    File,
    Sparkles,
    Play,
    ArrowRightLeft,
    Camera, ArrowBigLeft, ArrowBigRight
} from 'lucide-react';
import assetImg from '../assets/images/dispenser.png';
import { getActivityTypes, requestActivity } from "../actions/activityActions";
import { connect } from "react-redux";
import UIButton from "./utilities/UIButton";
import { Utility } from "../utilities/utility";
import * as Yup from 'yup';
import BottomSheet from "./bottom-sheet";
import { Form as IKForm, Formik, FormikHelpers } from "formik";
import { Form } from 'react-bootstrap';
import ScannerField from "./utilities/ScannerField";
import { SingleValue } from "react-select";
import { Option } from "../models/types";
import SearchableSelect from "./utilities/SearchableSelectBox";
import { useTranslation } from "react-i18next";
import FancyButton from "./utilities/FancyButton";
import '../assets/styles/App.css';
import { CameraRBModal } from "./utilities/NativeCamera";
import { upperFirst } from "lodash";
import AssetActivityStatus from './asset-activity-status';

// Matches the stable codes in App\Models\ActivityType.
const ACTIVITY_TYPE_CODES = { REPAIR: 1, CLEAN: 3, MOVE: 4 } as const;
const MAX_APP_BUTTONS = 3;
const REQUEST_SUBMIT_BUTTON_ID = 'request-sub-btn-1';
type RequestAction = number | 'other' | null;

const getRequestIcon = (type: IActivityType) => {
    switch (type.name_code) {
        case ACTIVITY_TYPE_CODES.REPAIR: return Wrench;
        case ACTIVITY_TYPE_CODES.CLEAN: return Sparkles;
        case ACTIVITY_TYPE_CODES.MOVE: return ArrowRightLeft;
        default: return List;
    }
};

interface Props {
    asset: IAsset;
    activityTypes: IActivityType[];
    dispatch: Function;
    showControls?: boolean;
}

interface FormValues {
    file: File | null;
    location_id: SingleValue<Option>;
    activity_type_id: number | undefined;
    person_id: SingleValue<Option>;
    remark: string;
    contact_person: string;
    priority: number | undefined;
}

export const Asset: React.FC<Props> = ({ asset, activityTypes, dispatch, showControls = true }) => {
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [action, setAction] = useState<RequestAction>(null);
    const [show, setShow] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingReset, setPendingReset] = useState<null | (() => void)>(null);

    const [iconShakeInterval, setIconShakeInterval] = useState<number | undefined>(undefined);
    const { i18n } = useTranslation();
    // @ts-ignore
    const t = (txt: string, defaultObj?: object) => i18n.t(txt, defaultObj);

    // IDs keep the order and selection stable when display names change.
    const buttonActivityTypes = activityTypes
        .filter((type) => type.show_in_app)
        .sort((a, b) => a.id - b.id)
        .slice(0, MAX_APP_BUTTONS);
    const buttonIds = new Set(buttonActivityTypes.map((type) => type.id));
    const otherActivityTypes = activityTypes.filter((type) => !buttonIds.has(type.id));
    const closeSheet = () => {
        setIsSheetOpen(false);
        setIconShakeInterval(undefined);
    };

    useEffect(function () {
        dispatch(getActivityTypes());
    }, [dispatch]);

    const circleStyle = (tone = 'primary'): React.CSSProperties => (
        {
            width: 30,
            height: 30,
            background: `var(--bs-${tone})`,
            borderRadius: '50%',
            color: 'var(--bs-white)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            flex: '0 0 auto',
        }
    );


    const initialValues: FormValues = {
        file: null, location_id: { value: '', label: '' }, person_id: null,
        contact_person: '', remark: '', priority: undefined, activity_type_id: undefined
    };

    const getActivityTypeForAction = (action: RequestAction, activityTypeId?: number) =>
        activityTypes.find((type) => type.id === (action === 'other' ? activityTypeId : action));

    const activityTypeNeedsPerson = (action: RequestAction, activityTypeId?: number) =>
        !!getActivityTypeForAction(action, activityTypeId)?.need_register_person;

    const handleSubmit = (values: FormValues, actions: FormikHelpers<FormValues>) => {
        handleRequestActivity(action, values);
        actions.setSubmitting(false);
    };

    const getValidationSchema = (action: RequestAction) =>
        Yup.object({
            file: Yup.mixed<File>()
                .nullable()
                .notRequired()
                .test('fileSize', 'File too large', (value) => !value || value.size <= 1024 * 1024)
                .test('fileFormat', 'Unsupported format', (value) =>
                    !value || ['image/jpeg', 'image/png'].includes(value.type)
                ),

            priority: Yup.number()
                .transform((val, orig) => (orig === '' ? undefined : Number(orig)))
                .typeError('Priority is required')
                .required('Priority is required'),

            ...(action === 'other'
                ? {
                    activity_type_id: Yup.number()
                        .transform((val, orig) => (orig === '' ? undefined : Number(orig)))
                        .typeError('Activity type is required')
                        .required('Activity type is required'),
                }
                : {}),

            person_id: Yup.mixed<Option>()
                .nullable()
                .test('personRequired', 'Person is required', function (value) {
                    return !activityTypeNeedsPerson(action, this.parent.activity_type_id) || !!value?.value;
                }),
        });

    const handleRequestActivity = (action: RequestAction, data: FormValues) => {
        const type = getActivityTypeForAction(action, data.activity_type_id);
        if (!type) return;

        dispatch(requestActivity({
            asset_id: asset.id,
            activity_type_id: type.id,
            remark: data.remark,
            contact_person: data.contact_person,
            priority: data.priority,
            person_id: data.person_id?.value,
            // Repair keeps the asset's location, independently of its display name.
            ...(type.name_code === ACTIVITY_TYPE_CODES.REPAIR ? {} : { location_id: data.location_id?.value }),
            file: data.file,
        }, REQUEST_SUBMIT_BUTTON_ID));
    };

    const handleAction = (action: RequestAction) => {
        setAction(action);
        setIsSheetOpen(true);
        setIconShakeInterval(4000);
    };

    let inspectionHasExpired = false;
    const coveredTo = Utility.formatAsDate(asset.last_inspection?.cover_to);
    if (coveredTo) {
        inspectionHasExpired = new Date() > coveredTo;
    }

    const removeImage = (setFieldValue: (n: string, v: any, s?: boolean) => void) => {
        setFieldValue('file', null, true);   // not "photo"
        if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
    };


    return (
        <div className="mt-4 border-0">
            <Card style={{ width: '100%', padding: '1px', border: 0, minHeight: '570px' }}>
                <Row className='gutterless-row' style={{ borderBottom: 'solid 1px #d2d2d2' }}>
                    <Card.Title className="text-center text-capitalize" style={{ fontSize: 17 }}>
                        {asset?.name || asset.asset_type?.name || asset.supplier?.name || asset.manufacturer?.name}
                    </Card.Title>
                </Row>
                <Row className='gutterless-row' style={{ borderBottom: '1px solid rgb(210, 210, 210)' }}>
                    {/* Details Section */}
                    <Col xs={8}>
                        <Card.Body className="p-0">
                            {/* Details List */}
                            <ListGroup variant="flush" className="border-0">
                                <ListGroup.Item className="d-flex align-items-center text-capitalize border-0">
                                    <span className="p-1 me-2" style={circleStyle()}><Stethoscope size={16} /></span> {asset.identifier}
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex align-items-center text-capitalize border-0">
                                    <span className="p-1 me-2" style={circleStyle()}><Barcode size={16} /></span> {asset.serial_number ? asset.serial_number : (<span style={{ fontWeight: 'bold', fontSize: '10px', paddingTop: '.25rem' }} className="text-danger">{t('serial number unknown')}</span>)}
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex align-items-center text-capitalize border-0" style={{ paddingRight: 40 }}>
                                    <span className="p-1 me-2" style={circleStyle()}><List size={16} /></span> {asset.manufacturer?.name ? Utility.shorten(asset.manufacturer.name, 12) : (<span style={{ fontWeight: 'bold', fontSize: '10px', paddingTop: '.25rem' }} className="text-danger">{t('manufacturer unknown')}</span>)}
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex align-items-center text-capitalize border-0" style={{ paddingRight: 40 }}>
                                    <span className="p-1 me-2" style={circleStyle()}><List size={16} /></span> {asset.global_gtin?.model ? Utility.shorten(asset.global_gtin?.model, 12) : (<span style={{ fontWeight: 'bold', fontSize: '10px', paddingTop: '.25rem' }} className="text-danger">{t('model unknown')}</span>)}
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex align-items-center text-capitalize border-0" style={{ paddingRight: 40 }}>
                                    <span className="p-1 me-2" style={circleStyle()}><MapPin size={16} /></span>  {asset.location?.name ? Utility.shorten(asset.location.name, 12) : (<span style={{ fontWeight: 'bold', fontSize: '10px', paddingTop: '.25rem' }} className="text-danger">{t('location unknown')}</span>)}
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex align-items-center text-capitalize border-0">
                                    <span className="p-1 me-2" style={circleStyle(inspectionHasExpired ? 'danger' : 'primary')}><ClipboardX size={16} /></span>
                                    {Utility.formatAsDate(asset.last_inspection?.executed_at)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })}
                                    {asset.last_inspection?.executed_at ? '' : (<span style={{ fontWeight: 'bold', fontSize: '10px', paddingTop: '.25rem' }} className="text-danger">{t('never inspected')}</span>)}
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex align-items-center text-capitalize border-0">
                                    <span className="p-1 me-2" style={circleStyle(inspectionHasExpired ? 'danger' : 'primary')}><ClipboardPen size={16} /></span>
                                    {Utility.formatAsDate(asset.last_inspection?.cover_to)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })}
                                    {asset.last_inspection?.cover_to ? '' : (<span style={{ fontWeight: 'bold', fontSize: '10px', paddingTop: '.25rem' }} className="text-danger">{t('never inspected')}</span>)}
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex align-items-center text-capitalize border-0">
                                    <span className="p-1 me-2" style={circleStyle(asset.status?.name ? 'primary' : 'danger')}><ThumbsUp size={16} /></span>
                                    <span className="text-primary-emphasis">
                                        {asset.status?.name ? asset.status?.name : (<span style={{ fontWeight: 'bold', fontSize: '10px', paddingTop: '.25rem' }} className="text-danger">{t('status.unknown')}</span>)}
                                    </span>
                                </ListGroup.Item>
                            </ListGroup>
                        </Card.Body>
                    </Col>

                    {/* Image Section */}
                    <Col xs={4}>
                        <div className="position-relative">
                            <div>
                                <Image
                                    src={asset.asset_image || assetImg}
                                    style={{ maxWidth: '100%', objectFit: 'cover', padding: '3px' }}
                                />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
                                {!!asset.manual &&
                                    <FancyButton id="manual-btn" icon={File} className="w-100"
                                        clickFunction={() => window.open(asset.manual)} label={t('manual')} />
                                }
                                {!!asset.video &&
                                    <FancyButton id="video-btn" icon={Play} className="w-100"
                                        clickFunction={() => window.open(asset.video)} label={t('video')} />
                                }
                                <AssetActivityStatus key={asset.id} assetId={asset.id} identifier={asset.identifier} />
                            </div>
                        </div>
                    </Col>
                </Row>

                {!!showControls && (<Card.Body style={{ padding: 0 }}>
                    <div className={'fancy-button-container'}>
                        {buttonActivityTypes.map((type) => (
                            <FancyButton key={type.id} id={`activity-type-${type.id}-btn`}
                                iconUrl={type.icon} icon={getRequestIcon(type)}
                                clickFunction={() => handleAction(type.id)} label={type.name} />
                        ))}
                        {otherActivityTypes.length > 0 && (
                            <FancyButton id="other-btn" icon={ArrowRightLeft}
                                clickFunction={() => handleAction('other')} label={t('Other')} />
                        )}
                    </div>

                </Card.Body>)}
            </Card>

            {!!showControls && (<BottomSheet isOpen={isSheetOpen} onClose={closeSheet}>
                <div className="mb-2">
                    <span style={{
                        marginBottom: '10px', marginRight: '10px',
                        fontSize: '18px', fontWeight: 'bolder',
                        padding: '5px', background: '#7aaafe'
                    }}>{t('inquiry')}</span>
                    <span style={{ fontStyle: 'italic', fontSize: '14px' }}>{t('request')} {action === 'other' ? t('Other') : getActivityTypeForAction(action)?.name}</span>
                </div>

                <Formik
                    initialValues={initialValues}
                    key={action ?? 'none'}
                    validationSchema={getValidationSchema(action)}
                    onSubmit={handleSubmit}
                    context={{ action }}
                // onReset={() => setIsSheetOpen(false)}
                >
                    {({
                        errors, touched, values,
                        handleBlur, handleChange,
                        setFieldValue, resetForm, dirty
                    }) => {
                        // Try to reset: if dirty, ask; else just reset immediately
                        const selectedActivityType = getActivityTypeForAction(action, values.activity_type_id);
                        const needsPerson = !!selectedActivityType?.need_register_person;
                        const tryReset = (e?: React.MouseEvent) => {
                            e?.preventDefault();
                            if (dirty || Object.keys(touched).length > 0) {
                                setPendingReset(() => () => {
                                    resetForm();
                                    setConfirmOpen(false);
                                    setIsSheetOpen(false);
                                });
                                setConfirmOpen(true);
                            } else {
                                resetForm();
                                setIsSheetOpen(false);
                            }
                        };
                        return (
                            <IKForm style={{ overflow: "auto", height: "100%" }}>

                                {action === 'other' ? (
                                    <>
                                        <Form.Label
                                            style={{ paddingTop: "10px", paddingBottom: "0", marginBottom: 0 }}>{t("activity.type")}</Form.Label>
                                        <Form.Select
                                            name="activity_type_id"
                                            value={values.activity_type_id ?? ''}
                                            onChange={(e) =>
                                                setFieldValue(
                                                    'activity_type_id',
                                                    e.target.value ? Number(e.target.value) : undefined
                                                )
                                            } isValid={touched.activity_type_id && !errors.activity_type_id}
                                            onBlur={handleBlur}                           // <-- mark as touched
                                            isInvalid={Boolean(touched.activity_type_id && errors.activity_type_id)}
                                        >
                                            <option>{t('select_activity_type')}</option>
                                            {otherActivityTypes.map((type) => (
                                                <option key={type.id} value={type.id}>{upperFirst(type.name)}</option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.activity_type_id}
                                        </Form.Control.Feedback>
                                    </>)
                                    : <></>}

                                {needsPerson && (
                                    <>
                                        <Form.Label
                                            className="required-label"
                                            style={{ paddingTop: "10px", paddingBottom: "0", marginBottom: 0 }}
                                        >
                                            {t("person")}
                                        </Form.Label>
                                        <SearchableSelect
                                            controller="person"
                                            label="person"
                                            name="person_id"
                                            wrap={false}
                                            value={values.person_id}
                                            onSelect={(value: SingleValue<Option>) => setFieldValue('person_id', value)}
                                        />
                                        {Boolean(touched.person_id && errors.person_id) && (
                                            <div className="invalid-feedback d-block">{errors.person_id as string}</div>
                                        )}
                                    </>
                                )}

                                <UIButton id='picture-taker' Icon={Camera} clickFunction={() => {
                                    setShow(true);
                                }} style={{ width: '100%', marginBottom: '5px', marginTop: '10px' }}
                                    label={t('add_picture_to_request')}></UIButton>

                                <CameraRBModal
                                    show={show}
                                    name="file"
                                    onHide={() => {
                                        setShow(false);
                                    }}
                                    onPreview={(url) => setPreviewUrl(url)}
                                    toFilename="photo.jpg"         // ensures it's a File
                                    fullscreen // or: "md-down"
                                    title="Take a photo"
                                    onCapture={(blob) => {
                                        // handle the captured Blob
                                        const fd = new FormData();
                                        fd.append("photo", blob, "photo.jpg");
                                        // fetch("/upload", { method: "POST", body: fd });
                                        setShow(false);
                                    }}
                                />
                                {previewUrl && (
                                    <div className="d-flex align-items-center gap-2 mb-3" style={{
                                        margin: '15px',
                                        position: 'relative',
                                        justifyContent: 'center'
                                    }}>
                                        <img
                                            src={previewUrl}
                                            alt="preview"
                                            style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 8 }}
                                        />
                                        <UIButton
                                            id="rm-img" variant="outline-danger"
                                            clickFunction={() => removeImage(setFieldValue)}
                                            Icon={Trash2}
                                            style={{
                                                position: 'absolute',
                                                top: '-10px',
                                                left: '75px',
                                                padding: '2px',
                                                background: 'white'
                                            }}
                                        ></UIButton>
                                    </div>
                                )}

                                <ScannerField scanOnload={false} label={t('location')} name='location_id'
                                    entity={asset.location} iconShakeInterval={iconShakeInterval} />

                                <Form.Label
                                    style={{ paddingTop: "10px", paddingBottom: "4px", marginBottom: 0 }}>{t('priority')}</Form.Label>
                                <Form.Select
                                    name="priority"
                                    value={values.priority ?? ''}               // keep controlled
                                    onChange={(e) => setFieldValue('priority', e.target.value)}
                                    isValid={touched.priority && !errors.priority}
                                    onBlur={handleBlur}                           // <-- mark as touched
                                    isInvalid={Boolean(touched.priority && errors.priority)}
                                >
                                    <option value="">{t('select priority')}</option>
                                    <option value="1">1 - {t('low')}</option>
                                    <option value="2">2 - {t('medium')}</option>
                                    <option value="3">3 - {t('high')}</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.priority}
                                </Form.Control.Feedback>

                                <Form.Label style={{
                                    paddingTop: "10px",
                                    paddingBottom: "4px", marginBottom: 0
                                }}>{t('Contact Person')}</Form.Label>
                                <Form.Control
                                    as="input"
                                    type="text"
                                    name="contact_person"
                                    value={values.contact_person}
                                    onChange={handleChange}
                                    isValid={touched.contact_person && !errors.contact_person}
                                />

                                <Form.Label
                                    style={{ paddingTop: "10px", paddingBottom: "4px", marginBottom: 0 }}>{t('remark')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    type="text"
                                    name="remark"
                                    value={values.remark}
                                    onChange={handleChange}
                                    isValid={touched.remark && !errors.remark}
                                />
                                {/*<UIButton variant='primary' type='submit' label={t('submit')} id='request-sub-btn' className="mt-3" />*/}
                                <div className={'fancy-button-container'} style={{ marginLeft: 0, paddingLeft: 0 }}>
                                    <FancyButton icon={ArrowBigLeft} reverse={true} type='button'
                                        clickFunction={tryReset} label={t('back')} id='request-res-btn'
                                        className="mt-3" />
                                    <FancyButton icon={ArrowBigRight} type='submit' label={t('submit')}
                                        id={REQUEST_SUBMIT_BUTTON_ID} className="mt-3" />
                                </div>
                                <Modal show={confirmOpen} onHide={() => setConfirmOpen(false)} centered>
                                    <Modal.Header closeButton>
                                        <Modal.Title>{t("Discard changes?")}</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        {t("You have unsaved changes. Do you want to reset the form?")}
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
                                            {t("Cancel")}
                                        </Button>
                                        <Button
                                            variant="danger"
                                            onClick={() => pendingReset?.()}
                                        >
                                            {t("Reset")}
                                        </Button>
                                    </Modal.Footer>
                                </Modal>
                            </IKForm>
                        )
                    }}
                </Formik>
            </BottomSheet>)}
        </div>
    );
}

const mapStateToProps = (state: any) => ({
    activityTypes: state.asset.activityTypes
});

export default connect(mapStateToProps)(Asset);
